import type {
  ConditionGroup,
  Contact,
  ContactListResponse,
  CreateContactInput,
  Household,
  NextPageParams,
  UpdateContactInput,
  ViewColumn,
} from "@/schema"

import { ApiContext, apiContextBlobFetch, apiContextFetch } from "@/hooks/api/base"
import { appendConditionGroupParams, appendViewSortParams } from "@/hooks/api/condition-group-params"

export function fetchContacts(
  context: ApiContext,
  viewFilterGroup?: ConditionGroup,
  nextPageParams?: NextPageParams,
  searchTerm?: string,
  viewSort?: { field?: string; direction?: "asc" | "desc" },
  minimal = false,
  listId?: string
) {
  const params = new URLSearchParams()

  if (nextPageParams) {
    params.append("page", nextPageParams.page.toString())
    params.append("per_page", nextPageParams.per_page.toString())
  }

  if (searchTerm) {
    params.append("search", searchTerm)
  }

  if (viewFilterGroup) {
    appendConditionGroupParams(params, viewFilterGroup)
  }

  appendViewSortParams(params, viewSort)

  if (minimal) {
    params.append("minimal", "true")
  }

  if (listId) {
    params.append("list_id", listId)
  }

  return apiContextFetch<ContactListResponse>(context, `contacts?${params.toString()}`, {
    method: "GET",
  })
}

export async function fetchContact(context: ApiContext, id: string): Promise<Contact> {
  return await apiContextFetch<Contact>(context, `contacts/${id}`)
}

export async function createContact(
  context: ApiContext,
  params: CreateContactInput,
  overrideBookId?: string
): Promise<Contact> {
  return await apiContextFetch<Contact>(context, "contacts", {
    method: "POST",
    body: JSON.stringify({ contact: params, override_book_id: overrideBookId }),
  })
}

export async function updateContact(context: ApiContext, id: string, params: UpdateContactInput): Promise<Contact> {
  return await apiContextFetch<Contact>(context, `contacts/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ contact: params }),
  })
}

export async function deleteContact(context: ApiContext, id: string): Promise<void> {
  await apiContextFetch<void>(context, `contacts/${id}`, {
    method: "DELETE",
  })
}

export interface BulkCreateContactsPayload {
  file_name?: string
  override_duplicates?: boolean
  records: Array<{
    household_id?: string
    contact_type?: string
    full_name?: string
    first_name?: string
    last_name?: string
    email?: string
    phone_number?: string
    status?: string
    address_line1?: string
    address_line2?: string
    address_city?: string
    address_state?: string
    address_zip?: string
    address_country_code?: string
    address_type?: string
    // Protected/additional person fields
    ssn?: string
    date_of_birth?: string
    gender?: string
    marital_status?: string
    job_title?: string
    salutation?: string
    suffix?: string
    middle_name?: string
    preferred_name?: string
    previous_name?: string
    designations?: string
    // Custom fields
    custom_fields?: Record<string, unknown>
  }>
}

export interface BulkCreateResponse {
  list_id: string
}

export function bulkCreateContacts(context: ApiContext, params: BulkCreateContactsPayload) {
  return apiContextFetch<BulkCreateResponse>(context, "contacts/bulk_create", {
    method: "POST",
    body: JSON.stringify(params),
  })
}

export interface AllContactIdsResponse {
  contact_ids: string[]
  total_count: number
}

export function fetchAllContactIds(context: ApiContext, viewFilterGroup?: ConditionGroup, searchTerm?: string) {
  const params = new URLSearchParams()

  if (searchTerm) {
    params.append("search", searchTerm)
  }

  if (viewFilterGroup) {
    appendConditionGroupParams(params, viewFilterGroup)
  }

  return apiContextFetch<AllContactIdsResponse>(context, `contacts/all_ids?${params.toString()}`, {
    method: "GET",
  })
}

export interface BulkUpdateContactsPayload {
  contact_ids: string[]
  status?: string
  tag_id?: string
}

export function bulkUpdateContacts(context: ApiContext, params: BulkUpdateContactsPayload) {
  return apiContextFetch<void>(context, "contacts/bulk_update", {
    method: "PATCH",
    body: JSON.stringify(params),
  })
}

export function bulkDestroyContacts(context: ApiContext, contactIds: string[]) {
  return apiContextFetch<void>(context, "contacts/bulk_destroy", {
    method: "DELETE",
    body: JSON.stringify({ contact_ids: contactIds }),
  })
}

export interface BulkMoveContactsToBookParams {
  contact_ids: string[]
  destination_book_id: string
}

export function bulkMoveContactsToBook(context: ApiContext, params: BulkMoveContactsToBookParams) {
  return apiContextFetch<void>(context, "contact_book_moves", {
    method: "POST",
    body: JSON.stringify(params),
  })
}

export interface ContactsCsvExportParams {
  viewColumns: ViewColumn[]
  conditionGroup: ConditionGroup
  viewSort?: { field?: string; direction?: "asc" | "desc" }
}

export async function downloadContactsCSV(context: ApiContext, params: ContactsCsvExportParams): Promise<Blob> {
  const searchParams = new URLSearchParams()

  params.viewColumns.forEach((vc, index) => {
    const prefix = `view_columns_attributes[${index}]`
    searchParams.append(`${prefix}[field]`, vc.field)
    if (vc.name) searchParams.append(`${prefix}[name]`, vc.name)
    searchParams.append(`${prefix}[is_visible]`, String(vc.is_visible))
    searchParams.append(`${prefix}[sort_order]`, String(vc.sort_order))
  })

  appendConditionGroupParams(searchParams, params.conditionGroup)
  appendViewSortParams(searchParams, params.viewSort)

  return apiContextBlobFetch(context, `contacts/csv_export?${searchParams.toString()}`)
}

export async function convertContactToHousehold(
  context: ApiContext,
  contactId: string,
  householdType: "Prospect" | "Client"
): Promise<Household> {
  return await apiContextFetch<Household>(context, `contacts/${contactId}/convert`, {
    method: "POST",
    body: JSON.stringify({ household_type: householdType }),
  })
}

export interface MergeContactResponse {
  id: string
}

export async function mergeContact(
  context: ApiContext,
  sourceContactId: string,
  targetContactId: string
): Promise<MergeContactResponse> {
  return await apiContextFetch<MergeContactResponse>(context, `contacts/${sourceContactId}/merge`, {
    method: "POST",
    body: JSON.stringify({ target_contact_id: targetContactId }),
  })
}
